import { ReactNode } from "react";

import styled from "styled-components";

export type Attribute = {
  name: string;
  label: string;
  value?: ReactNode;
  showMonoLabel?: boolean;
  showMonoValue?: boolean;
  showBoldLabel?: boolean;
  showBoldValue?: boolean;
};

export const toAttributes = (
  metadata?: Record<string, unknown>,
  annotateWhitespace: boolean = false
): Attribute[] | null => {
  return metadata
    ? Object.entries(metadata).map(([k, v]): Attribute => {
        let formattedValue: string | null = null;
        if (typeof v === "string") {
          formattedValue = v as string;
        } else {
          formattedValue = JSON.stringify(v);
        }

        if (annotateWhitespace && formattedValue) {
          formattedValue = formattedValue
            .replaceAll("\u0020", "•")
            .replaceAll("\t", "␉");
        }

        return {
          name: k,
          label: k,
          value: formattedValue,
          showMonoLabel: false,
          showMonoValue: true,
          showBoldLabel: false,
          showBoldValue: false,
        };
      })
    : null;
};

const AttributesViewContainer = styled.table<{ $columns: boolean }>`
  text-align: left;
  display: grid;
  ${({ $columns }) =>
    $columns
      ? `grid-template-columns: repeat(auto-fit, minmax(120px, max-content) minmax(min-content, 100px));`
      : `grid-template-columns: max-content 1fr;`}
`;

const AttributesViewBody = styled.tbody`
  display: contents;
`;

const AttributeRow = styled.tr`
  display: contents;
`;

const AttributeLabel = styled.td`
  font-style: italic;
  padding-right: 10px;
  min-width: 120px;
`;

const AttributeValue = styled.td<{ $truncate: boolean }>`
  padding-right: 30px;
  min-width: 0;
  min-width: 80px;
  ${({ $truncate }) =>
    $truncate
      ? `
        white-space: nowrap;
        overflow: hidden;
        text-overflow: ellipsis;
      `
      : ``}
`;

const AttributeText = ({
  showMono,
  showBold,
  children,
}: {
  showMono?: boolean;
  showBold?: boolean;
  children?: ReactNode;
}) => {
  if (showBold) {
    return showMono ? (
      <b>
        <code>{children}</code>
      </b>
    ) : (
      <b>{children}</b>
    );
  } else {
    return showMono ? <code>{children}</code> : children;
  }
};

const AttributeComponent = ({
  attribute: {
    label,
    value,
    showMonoLabel = false,
    showMonoValue = true,
    showBoldLabel = false,
    showBoldValue = false,
  },
  truncateValues = true,
  showEmpty,
}: {
  attribute: Attribute;
  showEmpty: boolean;
  truncateValues?: boolean;
}) => {
  return (
    (value || showEmpty) && (
      <AttributeRow>
        <AttributeLabel>
          <AttributeText showBold={showBoldLabel} showMono={showMonoLabel}>
            {label}
          </AttributeText>
        </AttributeLabel>
        <AttributeValue $truncate={truncateValues}>
          <AttributeText showBold={showBoldValue} showMono={showMonoValue}>
            {value}
          </AttributeText>
        </AttributeValue>
      </AttributeRow>
    )
  );
};

const StyledAttribute = styled(AttributeComponent)``;

export const AttributesView = ({
  attributes,
  className,
  showEmpty = true,
  columns = false,
  truncateValues = true,
}: {
  attributes?: Array<Attribute> | null;
  className?: string;
  showEmpty?: boolean;
  columns?: boolean;
  truncateValues?: boolean;
}) => {
  return (
    <AttributesViewContainer className={className} $columns={columns}>
      <AttributesViewBody>
        {attributes?.map((attribute) => {
          return (
            <StyledAttribute
              key={attribute.name}
              attribute={attribute}
              showEmpty={showEmpty}
              truncateValues={truncateValues}
            />
          );
        })}
      </AttributesViewBody>
    </AttributesViewContainer>
  );
};
